import { AxiosResponse } from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Button, CircularProgress, Divider } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import React, { useEffect, useMemo, useState } from 'react';
import { GridColDef, GridPreProcessEditCellProps, GridRowModel, useGridApiRef } from '@mui/x-data-grid';

import DataGrid from 'components/data-grid';
import TextField from 'components/text-field';
import NumberField from 'components/number-field';
import Autocomplete from 'components/autocomplete';
import LoadingSpinner from 'components/loading-spinner';
import DataGridToolbar from 'components/data-grid/data-grid-toolbar';
import RegistrationHeader from '../../../components/registration-header';
import LockPondContainer from 'views/production-systems/components/lock-pond';
import { FormValues, Option } from 'views/production-systems/types/stocking-form.type';
import PondLockTimer from 'views/production-systems/components/pond-lock-timer';
import DataGridSummary, { DataGridSummaryProps } from 'components/data-grid/data-grid-summary';

import Active from 'views/production-systems/assets/Active';
import Inactive from 'views/production-systems/assets/Inactive';

import { isValidDecimal, isValidInteger, isValidMeanWeight } from 'utils/validate-numbers';
import { NurseryItems } from 'views/production-systems/types/stocking.type';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import { useDataGridError } from 'views/production-systems/hooks/use-data-grid-error';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { calculateBiomass, calculateMeanWeight } from 'views/production-systems/utils/farm-overview';
import { getNursery, getStocking, putStocking } from 'views/production-systems/api/productionSystems';
import { UpdateStockingInput } from 'views/production-systems/features/registration-log/types/update-stocking-input.type';

type Props = {
  logId: number;
  eventNr: number;
  handleClose?: () => void;
  canEdit: boolean;
  dateCreated: Dayjs;
};

type ParamTypes = {
  globalSiteId: string;
};

type Row = {
  pondId: string;
  pondName: string;
  previousCycleId: number;
  cycleId: number;
  cycleName: string;
  startMw: number;
  targetMw: number;
  startQty: number;
  targetQty: number;
  targetEndDate: number;
  survivalRate: number;
};

const SURVIVAL_RATE_PERCENTAGE = 150;
const PAGE_NAME_ID = 'productionSystems.farmOverview.stocking';

const EditStocking: React.FC<Props> = ({ handleClose, logId, eventNr, canEdit = true, dateCreated }) => {
  const intl = useIntl();
  const apiRef = useGridApiRef();
  const queryClient = useQueryClient();
  const params = useParams<ParamTypes>();
  const snackbarDispatch = useSnackbarDispatch();
  const [rows, setRows] = useState<readonly Row[]>([]);
  const { lockedPonds, globalSiteId, lockPonds, releasePonds } = usePondsState();
  const { errorDictionary, changeErrorDictionary, isEditing, handleEditing, hasError } = useDataGridError();

  const [confirm, setConfirm] = useState<boolean>(false);
  const [readOnly, setReadOnly] = useState<boolean>(!canEdit);

  //#region FETCH DATA
  const { data: stocking, isFetching } = useGet(
    ['getStocking', globalSiteId, logId, eventNr],
    ({ queryKey }) => getStocking(String(queryKey[1]), Number(queryKey[2]), Number(queryKey[3]))
  );

  const { data: getNurseries } = useGet(['getNursery'], () => getNursery(globalSiteId), {
    enabled: !!globalSiteId,
  });
  //#endregion FETCH DATA

  //#region FORMS
  const defaultStockingValues = {
    deliveryTrackingCode: '',
    genetics: '',
    larvaeCost: '',
    maturation: 0,
    nauplius: 0,
    laboratory: 0,
  };

  const defaultFormValues = {
    ...defaultStockingValues,
    maturation: null,
    nauplius: null,
    laboratory: null,
  };

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: defaultFormValues,
    shouldUnregister: true,
  });
  //#endregion FORMS

  const formatNurserieOption = (nurseries: NurseryItems | undefined) => {
    return {
      id: nurseries ? String(nurseries?.id) : '',
      name: nurseries ? String(nurseries?.name) : '',
    };
  };

  useEffect(() => {
    const foundMaturation = getNurseries?.maturations.find((maturation) => maturation.id === stocking?.maturation);
    const foundNauplius = getNurseries?.nauplii.find((nauplii) => nauplii.id === stocking?.nauplius);
    const foundLaboratory = getNurseries?.laboratories.find((laboratory) => laboratory.id === stocking?.laboratory);

    if (!!stocking) {
      if (canEdit) lockPonds(stocking.cycles.map((cycle) => cycle.pondId));

      let rows: readonly any[] = stocking.cycles.map((cycle) => {
        return {
          pondId: cycle.pondId,
          pondName: cycle.pondName,
          cycleId: cycle.id,
          previousCycleId: cycle.previousCycleId,
          cycleName: cycle.cycleName,
          startMw: cycle.startMw,
          targetMw: cycle.targetMw,
          startQty: cycle.startQty,
          targetQty: cycle.targetQty,
          targetEndDate: dayjs(cycle.targetEndDate).diff(stocking.date, 'days'),
          survivalRate: cycle.survivalRate,
        };
      });
      reset({
        deliveryTrackingCode: stocking?.deliveryTrackingCode,
        genetics: stocking?.genetics,
        larvaeCost: String(stocking?.larvaeCost),
        maturation: formatNurserieOption(foundMaturation),
        nauplius: formatNurserieOption(foundNauplius),
        laboratory: formatNurserieOption(foundLaboratory),
      });
      setRows(rows);
    }
  }, [canEdit, getNurseries, lockPonds, reset, stocking]);

  const processRowUpdate = (newRow: GridRowModel<Row>) => {
    const updatedRow = { ...newRow, targetQty: Math.round(newRow.startQty * (newRow.survivalRate / 100)) };
    setRows(rows.map((row) => (row.pondId === newRow.pondId ? updatedRow : row)));
    return updatedRow;
  };

  const columns: GridColDef[] = useMemo(
    () =>
      [
        {
          field: 'pondName',
          flex: 2,
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.grid.destinationPond` }),
          editable: false,
          renderCell: ({ row }) => {
            return (
              <Box display={'flex'} gap={2} alignItems={'center'} width={'100%'}>
                {!!row.previousCycleId ? <Active titleAccess="Active" /> : <Inactive titleAccess="Inactive" />}
                <Box overflow={'hidden'} textOverflow={'ellipsis'} title={row.pondName}>
                  {row.pondName}
                </Box>
              </Box>
            );
          },
        },
        {
          field: 'cycleName',
          flex: 1,
          editable: false,
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.grid.cycleName` }),
          align: 'left',
          headerAlign: 'left',
        },
        {
          field: 'startQty',
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.grid.startQuantity` }),
          type: 'number',
          editable: !readOnly,
          align: 'right',
          headerAlign: 'right',
          flex: 1,
          preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
            const { value } = params.props;
            const hasError = !isValidInteger(value);
            changeErrorDictionary({ ...errorDictionary, [`${params.id}-startQty`]: hasError });
            return { ...params.props, error: hasError };
          },
        },
        {
          field: 'startMw',
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.grid.startMeanWeight` }),
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          editable: !readOnly,
          flex: 1,
          preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
            const { value } = params.props;
            const hasError = !isValidDecimal(value) || !isValidMeanWeight(value);
            changeErrorDictionary({ ...errorDictionary, [`${params.id}-startMw`]: hasError });
            return { ...params.props, error: hasError };
          },
        },
        {
          field: 'biomass',
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.grid.biomass` }),
          sortable: false,
          editable: false,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          flex: 1,
          valueGetter: (value, row) => calculateBiomass(row.startMw, row.startQty),
        },
        {
          field: 'targetMw',
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.grid.targetMeanWeight` }),
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          editable: !readOnly,
          flex: 1,
          preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
            const { value } = params.props;
            const hasError = !isValidDecimal(value) || !isValidMeanWeight(value);
            changeErrorDictionary({ ...errorDictionary, [`${params.id}-targetMw`]: hasError });
            return { ...params.props, error: hasError };
          },
        },
        {
          field: 'survivalRate',
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.grid.survivalRate` }),
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          editable: !readOnly,
          flex: 1,
          valueFormatter: (value: number) => `${value}%`,
          preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
            const { value } = params.props;
            const hasError = !isValidDecimal(value) || !(Number(value) <= SURVIVAL_RATE_PERCENTAGE);
            changeErrorDictionary({ ...errorDictionary, [`${params.id}-survivalRate`]: hasError });
            return { ...params.props, error: hasError };
          },
        },
        {
          field: 'targetEndDate',
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.grid.targetEndDate` }),
          flex: 1,
          align: 'right',
          headerAlign: 'right',
          editable: !readOnly,
          type: 'number',
          preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
            const hasError = !isValidInteger(params.props.value);
            changeErrorDictionary({ ...errorDictionary, [`${params.id}-targetEndDate`]: hasError });
            return { ...params.props, error: hasError };
          },
        },
        {
          field: 'targetQty',
          headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.grid.targetQuantity` }),
          flex: 1,
          editable: false,
          align: 'right',
          headerAlign: 'right',
          valueGetter: (value, row) => Math.round(row.startQty * (row.survivalRate / 100)),
        },
      ] as GridColDef[],
    [readOnly]
  );

  const summaryValues = useMemo(() => {
    let rate = rows.map((s) => s.survivalRate).reduce((prev, curr) => Number(prev) + Number(curr), 0) / rows.length;

    let newSummary = rows.reduce(
      (prev, curr, idx) => {
        let biomass = calculateBiomass(curr.startMw, curr.startQty);

        return {
          survivalRate: rate,
          currentBm: biomass + prev.currentBm,
          startMw: 0,
          targetMw: Number(prev.targetMw) + Number(curr.targetMw),
          startQty: Number(prev.startQty) + Number(curr.startQty),
          targetQty: Number(prev.targetQty) + Number(curr.targetQty),
        };
      },
      {
        survivalRate: rate,
        targetQty: 0,
        currentBm: 0,
        startQty: 0,
        targetMw: 0,
        startMw: 0,
      }
    );

    newSummary.startMw = calculateMeanWeight(newSummary.currentBm, newSummary.startQty);
    newSummary.targetMw = newSummary.targetMw / rows.length;
    newSummary.survivalRate = Number(newSummary.survivalRate.toFixed(2));
    return [
      {
        value: undefined, // Empty ListItem with 'hide' class
        type: undefined,
        hidden: true,
      },
      {
        value: newSummary.startQty,
        type: 'number',
        hidden: false,
      },
      {
        value: newSummary.startMw,
        type: 'number',
        hidden: false,
      },
      {
        value: newSummary.currentBm,
        type: 'number',
        hidden: false,
      },
      {
        value: newSummary.targetMw,
        type: 'number',
        hidden: false,
      },
      {
        value: `${newSummary.survivalRate}%`,
        type: 'string',
        hidden: false,
      },
      {
        value: undefined,
        type: undefined,
        hidden: true,
      },
      {
        value: newSummary.targetQty,
        type: 'number',
        hidden: false,
      },
    ];
  }, [rows]);
  //https://online.capio.com/noapp/?lo=true&s=dhc&de=true&hidd=6wa7qegdo7&utm_source=sms/
  //#endregion FORMS

  //#region HANDLERS

  const handleStockingSubmit = (data: FormValues) => {
    const { laboratory, maturation, nauplius } = formatAutocompleteData(data);

    updateStock.mutate({
      globalSiteId,
      data: {
        deliveryTrackingCode: data.deliveryTrackingCode || '',
        genetics: data.genetics || '',
        larvaeCost: Number(data.larvaeCost) || 0,
        maturation: maturation || 0,
        nauplius: nauplius || 0,
        laboratory: laboratory || 0,
        cycles: rows.map((row) => ({
          pondId: row.pondId,
          id: row.cycleId,
          previousCycleId: row.previousCycleId,
          cycleName: row.cycleName,
          startMw: row.startMw,
          targetMw: row.targetMw,
          startQty: row.startQty,
          targetQty: row.targetQty,
          survivalRate: row.survivalRate,
          targetEndDate: dayjs(stocking?.date).add(row.targetEndDate, 'days').format('YYYY-MM-DD'),
        })),
      } as UpdateStockingInput,
    });
  };

  const formatAutocompleteData = (data: FormValues) => {
    return {
      laboratory: Number(data.laboratory?.id),
      maturation: Number(data.maturation?.id ?? 0),
      nauplius: Number(data.nauplius?.id ?? 0),
    };
  };

  //#endregion HANDLERS

  //#region UPDATE DATA

  const updateStock = usePost<AxiosResponse, { globalSiteId: string; data: UpdateStockingInput }>(
    (request) => putStocking(params.globalSiteId, logId, request.data),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });

      stocking && releasePonds(stocking.cycles.map((cycle) => cycle.pondId));
      queryClient.invalidateQueries({
        queryKey: ['getRegistrations']
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  //#endregion UPDATE DATA

  //#region RENDERS

  const disableSubmit = useMemo(() => {
    return rows.some((row) => {
      return (
        !isValidInteger(row.survivalRate) ||
        !(Number(row.survivalRate) <= SURVIVAL_RATE_PERCENTAGE) ||
        !isValidInteger(row.targetEndDate) ||
        !isValidDecimal(row.targetMw) ||
        !isValidDecimal(row.startMw) ||
        !isValidInteger(row.startQty)
      );
    });
  }, [rows]);

  //#endregion RENDERS

  //#region HELPERS

  useEffect(() => {
    if (canEdit) {
      if (lockedPonds.notGranted.length > 0 && confirm) {
        setReadOnly(true);
      } else {
        setReadOnly(false);
      }
    }
  }, [confirm, canEdit, lockedPonds.notGranted]);

  const onKeyDown = (e: any) => e.stopPropagation();

  //#endregion HELPERS

  return isFetching ? (
    <LoadingSpinner />
  ) : lockedPonds?.notGranted?.length > 0 && !confirm ? (
    <LockPondContainer handleClose={handleClose} setConfirm={setConfirm} fromAdd={false} />
  ) : (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      {!readOnly && <PondLockTimer handleDialogClose={handleClose} />}
      <Box display={'flex'} flexDirection={'column'} gap={8}>
        <RegistrationHeader dateCreated={dateCreated} />
        <DataGrid
          rows={rows}
          apiRef={apiRef}
          columns={columns}
          getRowId={(row) => row.pondId}
          onCellEditStop={handleEditing}
          processRowUpdate={processRowUpdate}
          showToolbar
          slots={{ toolbar: DataGridToolbar, footer: DataGridSummary }}
          slotProps={{
            toolbar: {
              csvOptions: {
                fileName: `${intl.formatMessage({
                  id: 'productionSystems.registrationLog.registrationTypes.stocking',
                })}-${dayjs().format('DD/MM/YYYY')}`,
              },
            },
            footer: {
              summaryValues: summaryValues,
            } as DataGridSummaryProps,
          }}
          onRowClick={({ id }) => {
            const { field } = apiRef.current?.state.tabIndex.cell ?? {};
            apiRef.current?.getCellParams(id, field as string).isEditable && handleEditing();
          }}
        />
        <form onSubmit={handleSubmit(handleStockingSubmit)}>
          <Box display={'flex'} flexDirection={'column'}>
            <Divider sx={{ mb: 5 }} />
            <Box display={'flex'} gap={3}>
              <TextField
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.deliveredTrackingCode` })}
                dark
                controller={{
                  control: control,
                  name: 'deliveryTrackingCode',
                }}
                stopPropagation
                onKeyDown={onKeyDown}
              />
              <TextField
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.genetics` })}
                dark
                controller={{
                  control: control,
                  name: 'genetics',
                }}
                stopPropagation
                onKeyDown={onKeyDown}
              />
              <NumberField
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.larvaCost` })}
                dark
                controller={{
                  control: control,
                  name: 'larvaeCost',
                  rules: {
                    required: intl.formatMessage({ id: 'form.rules.required' }),
                    validate: {
                      isValidDecimal: (v: number) => isValidDecimal(v),
                    },
                  },
                }}
                stopPropagation
                onKeyDown={onKeyDown}
              />
              <Autocomplete
                dark
                fullWidth
                controller={{
                  name: 'laboratory',
                  control: control,
                  rules: {
                    required: intl.formatMessage({ id: 'form.rules.required' }),
                  },
                }}
                helperText={errors.laboratory?.message}
                multiple={false}
                disableCloseOnSelect={false}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.laboratoryPl` })}
                getOptionLabel={(option) => String((option as { name: string })?.name)}
                options={getNurseries?.laboratories.map((item) => ({ id: item.id, name: item.name })) || []}
              />
              <Autocomplete
                dark
                fullWidth
                controller={{
                  name: 'maturation',
                  control: control,
                }}
                multiple={false}
                disableCloseOnSelect={false}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.maturation` })}
                getOptionLabel={(option) => String((option as { name: string })?.name)}
                options={getNurseries?.maturations.map((item) => ({ id: item.id, name: item.name })) || []}
              />
              <Autocomplete
                dark
                fullWidth
                controller={{
                  name: 'nauplius',
                  control: control,
                }}
                multiple={false}
                disableCloseOnSelect={false}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.nauplius` })}
                getOptionLabel={(option) => String((option as { name: string })?.name)}
                options={getNurseries?.nauplii.map((item) => ({ id: item.id, name: item.name })) || []}
              />
            </Box>
            {!readOnly && (
              <Box display={'flex'} justifyContent={'flex-end'} mt={3}>
                <Button
                  type="submit"
                  disabled={updateStock.isPending || disableSubmit || isEditing || hasError}
                  startIcon={updateStock.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
                  <FormattedMessage id={'general.confirm'} />
                </Button>
              </Box>
            )}
          </Box>
        </form>
      </Box>
    </Box>
  );  
};

export default React.memo(EditStocking);
